<template>
  <SettingsCard title="Minhas empresas" :description="companiesLabel">
    <div v-if="isLoading" class="flex flex-col gap-3">
      <div v-for="n in 3" :key="n" class="h-16 rounded-xl bg-gray-100 animate-pulse" />
    </div>

    <div v-else-if="!myCompanies.length" class="py-8 text-center text-gray-500">
      <v-icon size="32" color="grey-lighten-1" class="mb-2">{{ mdiOfficeBuildingOutline }}</v-icon>
      <p>Você ainda não está vinculado a nenhuma empresa.</p>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div
        v-for="company in myCompanies"
        :key="company.id"
        class="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-4"
        data-testid="organization-card"
      >
        <div class="flex items-center gap-3 min-w-0">
          <v-avatar color="primary" size="44">
            <span class="text-sm font-bold text-white">{{ getInitials(company.name) }}</span>
          </v-avatar>
          <div class="min-w-0">
            <p class="text-base font-medium text-gray-900 truncate">{{ company.name }}</p>
            <p class="text-xs text-gray-500">Empresa #{{ company.id }}</p>
          </div>
        </div>

        <button
          v-if="canManageAccess"
          type="button"
          class="shrink-0 text-sm font-medium text-primary hover:underline"
          :data-testid="`organization-manage-access-${company.id}`"
          @click="goToAccessManager"
        >
          Gerenciar acesso
        </button>
      </div>
    </div>
  </SettingsCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { mdiOfficeBuildingOutline } from '@mdi/js'

import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import PATHS from '@/router/paths'

import SettingsCard from '../atoms/SettingsCard.vue'
import { getInitials } from '../../helpers/profile'

const router = useRouter()
const { flags } = useFeatureFlags()
const companyStore = useCompanyStore()
const { myCompanies } = storeToRefs(companyStore)

const isLoading = ref(false)

const canManageAccess = computed(() => flags.enabledDSPoliciesPermissions)

const companiesLabel = computed(() => {
  const count = myCompanies.value.length
  if (!count) return 'Empresas às quais você tem acesso.'
  return count === 1 ? '1 empresa vinculada.' : `${count} empresas vinculadas.`
})

const goToAccessManager = () => router.push(PATHS.ACCESS_MANAGER)

onMounted(async () => {
  isLoading.value = true
  try {
    await companyStore.getMyCompanies()
  } finally {
    isLoading.value = false
  }
})
</script>
